import type { SvgIconProps } from "@mui/material";
import { Box, Button, Card, CardActions, CardContent, Stack, Switch, Typography } from "@mui/material";
import type { FC } from "react";
import { useNavigate } from "react-router";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import type { IntegrationAppStatus } from "../../../../api/types/clientTypes";
import ApplicationStatusTag from "../../../common/ApplicationStatusTag";
import NavigateByLink from "../../../common/NavigateByLink";
import CardTitle from "./CardTitle";

interface Props {
  title: string;
  text: string;
  Icon?: FC<SvgIconProps>;
  logoSrc?: string;
  appStatus: IntegrationAppStatus;
  configurationRoute?: string;
  configurationLink?: string;
  moreInfoLink?: string;
  cardId: string; // Needed to link Intercom
}

const IntegrationsCard = ({
  title,
  appStatus,
  text,
  Icon,
  logoSrc,
  moreInfoLink,
  configurationRoute,
  configurationLink,
  cardId,
}: Props) => {
  const navigate = useNavigate();

  const handleConfigureClick = () => {
    if (configurationRoute) {
      navigate(configurationRoute);
    } else if (configurationLink) {
      window.open(configurationLink, "_blank");
    }
  };

  return (
    <Card variant="outlined" sx={(theme) => ({ width: theme.spacing(53), p: theme.spacing(1) })}>
      <CardContent>
        <Stack spacing={2} sx={(theme) => ({ height: theme.spacing(15) })}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <CardTitle id={cardId} title={title} Icon={Icon} logoSrc={logoSrc} />
            <ApplicationStatusTag appStatus={appStatus} />
          </Box>
          <Typography color="textSecondary">{text}</Typography>
        </Stack>
      </CardContent>
      <CardActions sx={(theme) => ({ ml: theme.spacing(1), pt: 0, minHeight: theme.spacing(5) })}>
        <Stack direction="row" spacing={1}>
          {(configurationRoute || configurationLink) && appStatus !== undefined && (
            <Button variant="outlined" color="secondary" onClick={handleConfigureClick}>
              Configure
            </Button>
          )}
          {moreInfoLink && <NavigateByLink title="More Info" color="secondary" href={moreInfoLink} openInNewTab />}
        </Stack>
        <HorizontalFill />
        {appStatus !== "NoStatus" && <Switch checked={appStatus === "Published" || appStatus === "Error"} disabled />}
      </CardActions>
    </Card>
  );
};

export default IntegrationsCard;
